/** Core intermediate representation types for Ex-Mermaid. */

export type Direction = 'TB' | 'TD' | 'LR' | 'RL' | 'BT' | 'auto' | 'compact' | 'spaced';

export interface LayoutConfig {
  direction: Exclude<Direction, 'auto' | 'compact' | 'spaced'>; // resolved (never 'auto'/'compact'/'spaced')
  nodesep: number;
  ranksep: number;
  edgesep: number;
  marginx: number;
  marginy: number;
  ranker: 'network-simplex' | 'tight-tree' | 'longest-path';
  align?: 'UL' | 'UR' | 'DL' | 'DR';
}

export type DiagramType =
  | 'flowchart'
  | 'class'
  | 'state'
  | 'er'
  | 'mindmap'
  | 'gantt'
  | 'timeline';

export type InputFormat = DiagramType | 'drawio-xml';

export type OutputFormat =
  | 'drawio-flowchart'
  | 'drawio-activity'
  | 'drawio-class'
  | 'drawio-state'
  | 'drawio-er'
  | 'drawio-mindmap'
  | 'drawio-gantt'
  | 'drawio-timeline'
  | 'mermaid';

export enum NodeShape {
  Process = 'process',
  Decision = 'decision',
  StartCircle = 'start-circle',
  EndCircle = 'end-circle',
  Datastore = 'datastore',
  ForkBar = 'fork-bar',
  JoinBar = 'join-bar',
  MergeDiamond = 'merge-diamond',
  Subroutine = 'subroutine',
  ClassBox = 'class-box',
  ErEntity = 'er-entity',
  Stadium = 'stadium',
}

export interface IRNode {
  id: string;
  label: string;
  shape: NodeShape;
  laneId?: string;
  width: number;
  height: number;
  x?: number;
  y?: number;
  /** User-defined metadata (e.g. mermaid shape hint) */
  meta?: Record<string, unknown>;
}

export interface IREdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  dashed?: boolean;
  /** true when source and target are in different lanes */
  crossLane?: boolean;
  /** User-defined metadata (e.g. relationship type, cardinality) */
  meta?: Record<string, unknown>;
}

export interface IRLane {
  id: string;
  title: string;
  nodeIds: string[];
  x: number;
  y: number;
  width: number;
  height: number;
  /** For nested composite states: ID of the parent lane that contains this one */
  parentLaneId?: string;
}

export interface IRGraph {
  nodes: Map<string, IRNode>;
  edges: IREdge[];
  lanes: IRLane[];
  direction: Direction;
  diagramType: DiagramType;
}

export interface LayoutResult {
  graph: IRGraph;
  containerX: number;
  containerY: number;
  containerWidth: number;
  containerHeight: number;
}

export interface ConvertOptions {
  inputFormat?: InputFormat;
  outputFormat: OutputFormat;
  direction: Direction;
  colors: ColorPalette;
}

export interface ColorPalette {
  colors: string[];
  opacity: number; // 0–1
}

export function createEmptyGraph(
  direction: Direction = 'TD',
  diagramType: DiagramType = 'flowchart',
): IRGraph {
  return {
    nodes: new Map(),
    edges: [],
    lanes: [],
    direction,
    diagramType,
  };
}
